import { Checkbox, Col, Flex, Row, Spin, Typography } from "antd";
import { useEffect, useMemo } from "react";
import { formatUnidad } from "utils";
import useGetListaPrecio from "./useGetListaPrecio";

const { Text } = Typography;

const ListaPrecios = ({ precioId, unidadMedida, checked, setChecked, styles }) => {
  const { tramos, articulos, unidades, loadingListaPrecio, fetchListaPrecio } = useGetListaPrecio();
  const precio = useMemo(
    () => checked?.find(x => x.id === precioId) || { tramos: [], articulos: [], unidades: [] },
    [checked, precioId]
  );
  const sinTramos = precio?.tramos?.length === 0;
  const sinArticulos = precio?.articulos?.length === 0;

  const ensurePrecio = (precioId) => {
    const index = checked?.findIndex(x => x.id === precioId);

    if (index === -1) {
      const nuevo = {
        id: precioId,
        tramos: [],
        articulos: [],
        unidades: []
      };

      let newIndex = 0;

      setChecked(prev => {
        const arr = [...prev, nuevo];
        newIndex = arr.length - 1;
        return arr;
      });

      return newIndex;
    }

    return index;
  };

  const toggleItem = (tipo, itemId, isChecked) => {
    const index = ensurePrecio(precioId);

    setChecked(prev => {
      const copia = [...prev];
      const arr = copia[index][tipo];

      // actualizar lista
      const updated = isChecked
        ? [...new Set([...arr, itemId])]
        : arr.filter(id => id !== itemId);

      if (tipo === "tramos" && updated.length === 0) {
        copia[index].articulos = [];
        copia[index].unidades = [];
      }

      if (tipo === "articulos" && updated.length === 0) {
        copia[index].unidades = [];
      }

      // aplicar cambio
      copia[index] = {
        ...copia[index],
        id: precioId,  // ← agregado nuevamente
        [tipo]: updated
      };

      // borrar entrada si queda vacío completamente
      const empty =
        copia[index].tramos.length === 0 &&
        copia[index].articulos.length === 0 &&
        copia[index].unidades.length === 0;

      if (empty) {
        return copia.filter((_, i) => i !== index);
      }

      return copia;
    });
  };

  const selectAll = (tipo, items, isChecked) => {
    const index = ensurePrecio(precioId);

    setChecked(prev => {
      const copia = [...prev];
      const precioActual = { ...copia[index] };

      // si es UNIDADES → usar los valores directos
      const updated =
        tipo === "unidades"
          ? (isChecked ? [...items] : [])
          : (isChecked ? items.map(i => i.id) : []);

      precioActual[tipo] = updated;

      // reglas de cascada
      if (tipo === "tramos" && updated.length === 0) {
        precioActual.articulos = [];
        precioActual.unidades = [];
      }

      if (tipo === "articulos" && updated.length === 0) {
        precioActual.unidades = [];
      }

      // borrar si está vacío
      const empty =
        precioActual.tramos.length === 0 &&
        precioActual.articulos.length === 0 &&
        precioActual.unidades.length === 0;

      if (empty) {
        return copia.filter((_, i) => i !== index);
      }

      copia[index] = precioActual;
      return copia;
    });
  };

  const count = (tipo) => precio?.[tipo]?.length || 0;

  useEffect(() => {
    if (precioId) fetchListaPrecio(precioId);
  }, [precioId]);

  useEffect(() => {
    if (!precioId) return;

    // crear estructura vacía si no existe
    const index = ensurePrecio(precioId);

    // si YA tiene datos → no autoseleccionar
    const precio = checked[index];
    if (
      precio?.tramos?.length > 0 ||
      precio?.articulos?.length > 0 ||
      precio?.unidades?.length > 0
    ) {
      return;
    }

    // AUTOCHECK 🔥
    setChecked(prev => {
      const copia = [...prev];

      copia[index] = {
        id: precioId,
        tramos: tramos?.map(t => t.id) || [],
        articulos: articulos?.map(a => a.id) || [],
        unidades: unidades?.map(u => u) || []
      };

      return copia;
    });
  }, [precioId, tramos, articulos, unidades]);

  return (
    <Spin spinning={loadingListaPrecio}>
      <Row gutter={24}>
        {/* TRAMOS */}
        <Col xs={9} style={styles.colStyle}>
          <Flex justify="space-between" style={styles.styleSticky}>
            <Flex align="baseline" gap={8}>
              <Checkbox
                checked={precio?.tramos?.length === tramos?.length}
                indeterminate={precio?.tramos?.length > 0 && precio?.tramos?.length < tramos?.length}
                onChange={e => selectAll("tramos", tramos, e.target.checked)}
              />
              <Typography.Text>DISTANCIAS</Typography.Text>
            </Flex>

            <Text type="secondary">
              {count("tramos")}/{tramos?.length}
            </Text>
          </Flex>

          <Flex vertical gap={6} style={styles.colPadding}>
            {tramos?.map(t => (
              <Checkbox
                key={t.id}
                checked={precio?.tramos?.includes(t.id)}
                onChange={e => toggleItem("tramos", t.id, e.target.checked)}
              >
                {t.descripcion}
              </Checkbox>
            ))}
          </Flex>
        </Col>

        {/* ARTICULOS */}
        <Col
          xs={9}
          style={{
            ...styles.colStyle,
            borderLeft: "1px solid #D9D9D950",
            borderRight: "1px solid #D9D9D950"
          }}
        >
          <Flex justify="space-between" style={styles.styleSticky}>
            <Flex align="baseline" gap={8}>
              <Checkbox
                disabled={sinTramos}
                checked={precio?.articulos?.length === articulos?.length}
                indeterminate={precio?.articulos?.length > 0 && precio?.articulos?.length < articulos?.length}
                onChange={e => selectAll("articulos", articulos, e.target.checked)}
              />
              <Typography.Text>ARTÍCULOS</Typography.Text>
            </Flex>

            <Text type="secondary">
              {count("articulos")}/{articulos?.length}
            </Text>
          </Flex>

          <Flex vertical gap={6} style={styles.colPadding}>
            {articulos?.map(a => (
              <Checkbox
                key={a.id}
                disabled={sinTramos}
                checked={precio?.articulos?.includes(a.id)}
                onChange={e => toggleItem("articulos", a.id, e.target.checked)}
              >
                {a.nombre}
              </Checkbox>
            ))}
          </Flex>
        </Col>

        {/* UNIDADES */}
        <Col xs={6} style={styles.colStyle}>
          <Flex justify="space-between" style={styles.styleSticky}>
            <Flex align="baseline" gap={8}>
              <Checkbox
                disabled={sinTramos || sinArticulos}
                checked={precio?.unidades?.length === unidades?.length}
                indeterminate={precio?.unidades?.length > 0 && precio?.unidades?.length < unidades?.length}
                onChange={e => selectAll("unidades", unidades, e.target.checked)}
              />
              <Typography.Text>UNIDADES</Typography.Text>
            </Flex>

            <Text type="secondary">
              {count("unidades")}/{unidades?.length}
            </Text>
          </Flex>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(100px, 1fr))",
              gap: "6px 12px",
              ...styles.colPadding,
            }}
          >
            {unidades?.map(unidad => (
              <Checkbox
                key={unidad}
                disabled={sinTramos || sinArticulos}
                checked={precio?.unidades?.includes(unidad)}
                onChange={e => toggleItem("unidades", unidad, e.target.checked)}
              >
                {formatUnidad(unidadMedida, unidad)}
              </Checkbox>
            ))}
          </div>
        </Col>
      </Row>
    </Spin>
  );
};

export default ListaPrecios;
